import type { CSSProperties, Key, ReactNode, Ref } from 'react'
import { useImperativeHandle, useRef } from 'react'
import { ScrollArea } from '#components/scroll-area'
import {
	useVirtualization,
	type VirtualAxis,
	type VirtualizationApi,
	type VirtualThreshold
} from '#hooks/use-virtualization'
import { cn } from '#lib/utils'

export interface VirtualListProps<T> {
	items: T[]
	children: (item: T, index: number) => ReactNode
	axis?: VirtualAxis
	estimateSize?: (index: number) => number
	overscan?: number
	initialOffset?: number | (() => number)
	getItemKey?: (item: T, index: number) => Key
	threshold?: VirtualThreshold
	onStartReached?: () => unknown
	onEndReached?: () => unknown
	className?: string
	header?: ReactNode
	footer?: ReactNode
	ref?: Ref<VirtualizationApi>
}

function getItemKey<T>(props: VirtualListProps<T>, index: number) {
	const item = props.items[index]

	if (item === undefined) {
		return index
	}

	return props.getItemKey?.(item, index) ?? index
}

export function VirtualList<T>(props: VirtualListProps<T>) {
	const scrollRef = useRef<HTMLDivElement>(null)
	const axis = props.axis ?? 'vertical'
	const horizontal = axis === 'horizontal'
	const virtual = useVirtualization(
		horizontal
			? {
					scrollRef,
					horizontal: {
						count: props.items.length,
						estimateSize: props.estimateSize,
						overscan: props.overscan,
						initialOffset: props.initialOffset,
						getItemKey: (index) => getItemKey(props, index),
						threshold: props.threshold,
						onStartReached: props.onStartReached,
						onEndReached: props.onEndReached
					}
				}
			: {
					scrollRef,
					vertical: {
						count: props.items.length,
						estimateSize: props.estimateSize,
						overscan: props.overscan,
						initialOffset: props.initialOffset,
						getItemKey: (index) => getItemKey(props, index),
						threshold: props.threshold,
						onStartReached: props.onStartReached,
						onEndReached: props.onEndReached
					}
				}
	)
	const list = virtual[axis]
	const style: CSSProperties & { '--virtual-list-size': string } = {
		'--virtual-list-size': `${list.totalSize}px`
	}

	useImperativeHandle(props.ref, () => virtual.api, [virtual.api])

	return (
		<ScrollArea
			className={cn(
				'h-full w-full [&_[data-slot=scroll-area-viewport]]:[overflow-anchor:none]',
				props.className
			)}
			orientation={horizontal ? 'horizontal' : 'vertical'}
			style={style}
			viewportRef={scrollRef}
		>
			<div
				style={{
					display: horizontal ? 'flex' : 'block',
					height: horizontal ? '100%' : undefined,
					width: horizontal ? 'max-content' : '100%'
				}}
			>
				{props.header && (
					<div style={{ flexShrink: 0 }}>{props.header}</div>
				)}
				<div
					style={{
						height: horizontal ? '100%' : list.totalSize,
						width: horizontal ? list.totalSize : '100%',
						position: 'relative',
						flexShrink: 0
					}}
				>
					{list.items.map((item) => {
						// Virtual range can lag a frame behind a shrinking items array; skip stale indices.
						if (props.items[item.index] === undefined) {
							return null
						}

						return (
							<div
								data-index={item.index}
								key={item.key}
								ref={list.virtualizer.measureElement}
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									height: horizontal ? '100%' : undefined,
									width: horizontal ? undefined : '100%',
									transform: horizontal
										? `translateX(${item.start}px)`
										: `translateY(${item.start}px)`
								}}
							>
								{props.children(
									props.items[item.index],
									item.index
								)}
							</div>
						)
					})}
				</div>
				{props.footer && (
					<div style={{ flexShrink: 0 }}>{props.footer}</div>
				)}
			</div>
		</ScrollArea>
	)
}
